import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { BookOpen, Download, Lock, ClipboardList, Compass, BarChart3, Flag } from "lucide-react";
import { MaskedLine, Reveal, Overline } from "@/components/Reveal";
import { API, trackEvent, formatApiError } from "@/lib/api";

const ASSET_ICONS = {
  "interpretive-guide": BookOpen,
  "practice-par-scorecard": ClipboardList,
  "par-practice-guide": Compass,
  "performance-dashboard": BarChart3,
  "round-analysis-scorecard": Flag,
};

export default function Members() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [assets, setAssets] = useState(null);
  const [tier, setTier] = useState("");
  const [referredBy, setReferredBy] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Member Assets — Wired 2 Win Golf";
    trackEvent("page_view", { page: "members" });
  }, []);

  const unlock = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/members/access`, { email });
      setAssets(data.assets);
      setTier(data.tier);
      setReferredBy(data.referred_by || "");
      trackEvent("member_access_granted");
    } catch (err) {
      setError(formatApiError(err));
      trackEvent("member_access_denied");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="members-page" className="pt-32 lg:pt-40 min-h-[100svh]">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-28">
        <Overline className="mb-6"><MaskedLine>Member area</MaskedLine></Overline>
        <h1 className="font-display font-black tracking-tighter leading-[0.92] text-5xl sm:text-7xl uppercase max-w-4xl">
          <MaskedLine delay={0.15}>Your Wired2Win</MaskedLine>
          <MaskedLine delay={0.3}><span className="text-volt">training assets.</span></MaskedLine>
        </h1>

        {!assets ? (
          <Reveal delay={0.45}>
            <form onSubmit={unlock} className="mt-12 max-w-md border border-white/10 bg-ink p-8" data-testid="members-unlock-form">
              <Lock className="w-6 h-6 text-volt mb-5" />
              <p className="text-white/60 text-sm leading-relaxed mb-6">
                Enter the email you used at checkout to unlock your downloads.
              </p>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                data-testid="members-email-input"
                aria-label="Email used at checkout"
                className="w-full bg-white/5 border border-white/15 px-4 py-3.5 text-sm placeholder:text-white/30 focus:outline-none focus:border-volt transition-colors mb-4"
              />
              {error && (
                <p data-testid="members-error" className="text-red-400 text-xs mb-4">
                  {error} Just joined? Try again in a minute — or email dbisbee@egolfgroup.com.
                </p>
              )}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={loading}
                data-testid="members-unlock-button"
                className="w-full bg-volt text-obsidian font-display font-bold px-6 py-4 hover:bg-white transition-colors disabled:opacity-50"
              >
                {loading ? "Checking..." : "Unlock My Assets"}
              </motion.button>
            </form>
          </Reveal>
        ) : (
          <Reveal>
            <p data-testid="members-tier" className="mt-10 text-white/50 text-sm">
              Membership active{tier !== "unknown" ? ` — ${tier} plan` : ""}. Download anytime.
            </p>
            {referredBy && (
              <p data-testid="members-referred-by" className="mt-2 text-volt text-sm font-semibold">
                Referred by {referredBy} — welcome to the program.
              </p>
            )}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl" data-testid="members-assets">
              {assets.map((a) => {
                const Icon = ASSET_ICONS[a.key] || BookOpen;
                return (
                  <motion.div
                    key={a.key}
                    whileHover={{ y: -4 }}
                    transition={{ duration: 0.25 }}
                    className="border border-volt/40 bg-volt/5 p-8 flex flex-col"
                    data-testid={`asset-card-${a.key}`}
                  >
                    <Icon className="w-8 h-8 text-volt mb-5" />
                    <h2 className="font-display font-bold text-2xl tracking-tight mb-3">{a.name}</h2>
                    <p className="text-white/60 text-sm leading-relaxed mb-8">{a.desc}</p>
                    <a
                      href={`${API}/members/assets/${a.key}?email=${encodeURIComponent(email)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`asset-download-${a.key}`}
                      onClick={() => trackEvent("member_asset_download", { asset: a.key })}
                      className="group mt-auto inline-flex items-center justify-center gap-2 bg-volt text-obsidian font-display font-bold px-6 py-4 hover:bg-white transition-colors"
                    >
                      <Download className="w-4 h-4" /> Download PDF
                    </a>
                  </motion.div>
                );
              })}
            </div>
            <p className="mt-8 text-white/40 text-xs">New training assets are added regularly — they're all included in your membership.</p>
          </Reveal>
        )}
      </section>
    </main>
  );
}
